import type {
  EditorJsBlock,
  EditorJsData,
} from '#pages/document/editorjs-types';
import { normalizeGalleryBlockData } from '#pages/document/editorjs-types';
import { formatDocumentName } from '#utils/format-document-name';

import { stripEditorInlineMarkup } from './editor-files-section';
import { NP_DOCUMENT_TITLE_TAG } from './markdown-document-title';
import { serializeMarkdownFilesBlock } from './markdown-files';
import { serializeMarkdownGalleryBlock } from './markdown-gallery';

export interface ConvertEditorJsToMarkdownError extends Error {
  details?: Record<string, unknown>;
}

class EditorJsToMarkdownError extends Error {
  details: Record<string, unknown>;

  constructor(message: string, details: Record<string, unknown>) {
    super(message);
    this.name = 'EditorJsToMarkdownError';
    this.details = details;
  }
}

function fail(message: string, details: Record<string, unknown>): never {
  throw new EditorJsToMarkdownError(message, details);
}

function normalizeTitleText(value: string): string {
  return stripEditorInlineMarkup(normalizePlainText(value))
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizePlainText(value: string): string {
  return value.replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');
}

function getExpectedTitle(displayPath: string): string {
  const segments = formatDocumentName(displayPath.trim());
  const last = segments.length > 0 ? segments[segments.length - 1] : '';
  return (last || displayPath || 'Untitled').trim();
}

function getObjectRecord(
  value: unknown,
  ctx: { blockIndex: number; blockType: string; field: string },
): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    fail('Conversion encountered malformed block data.', {
      ...ctx,
      value,
    });
  }

  return value as Record<string, unknown>;
}

function getRequiredString(
  rec: Record<string, unknown>,
  key: string,
  ctx: { blockIndex: number; blockType: string },
): string {
  const value = rec[key];
  if (typeof value !== 'string') {
    fail(`Conversion expected a string at "${key}".`, {
      ...ctx,
      key,
      data: rec,
    });
  }

  return value;
}

function escapePipe(value: string): string {
  return value.replace(/\|/g, '\\|');
}

function renderParagraph(block: EditorJsBlock, blockIndex: number): string {
  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  return normalizePlainText(
    getRequiredString(data, 'text', {
      blockIndex,
      blockType: block.type,
    }),
  );
}

function renderHeader(block: EditorJsBlock, blockIndex: number): string {
  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  const text = getRequiredString(data, 'text', {
    blockIndex,
    blockType: block.type,
  });
  const rawLevel = data.level;
  if (
    typeof rawLevel !== 'number' ||
    !Number.isInteger(rawLevel) ||
    rawLevel < 1 ||
    rawLevel > 6
  ) {
    fail('Conversion encountered an unsupported header level.', {
      blockIndex,
      blockType: block.type,
      data,
    });
  }

  return `${'#'.repeat(rawLevel)} ${normalizePlainText(text)}`;
}

type ListItemNode = string | { content: string; items?: ListItemNode[] };

function renderListItems(
  items: unknown,
  style: 'ordered' | 'unordered',
  ctx: { blockIndex: number; blockType: string },
  depth = 0,
): string[] {
  if (!Array.isArray(items)) {
    fail('Conversion expected list items to be an array.', {
      ...ctx,
      items,
    });
  }

  const marker = style === 'ordered' ? '1.' : '-';
  return items.flatMap((item) => {
    const indent = '  '.repeat(depth);
    if (typeof item === 'string') {
      return `${indent}${marker} ${normalizePlainText(item)}`;
    }

    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      fail('Conversion encountered an unsupported list item shape.', {
        ...ctx,
        item,
      });
    }

    const rec = item as Record<string, unknown>;
    const content = rec.content;
    const nestedItems = rec.items;
    if (typeof content !== 'string') {
      fail(
        'Conversion encountered a nested list item without string content.',
        {
          ...ctx,
          item,
        },
      );
    }

    const lines = [`${indent}${marker} ${normalizePlainText(content)}`];
    if (nestedItems !== undefined) {
      lines.push(
        ...renderListItems(
          nestedItems as ListItemNode[],
          style,
          ctx,
          depth + 1,
        ),
      );
    }

    return lines;
  });
}

function renderList(block: EditorJsBlock, blockIndex: number): string {
  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  const rawStyle = data.style;
  if (rawStyle !== 'ordered' && rawStyle !== 'unordered') {
    fail('Conversion encountered an unsupported list style.', {
      blockIndex,
      blockType: block.type,
      data,
    });
  }

  return renderListItems(data.items, rawStyle, {
    blockIndex,
    blockType: block.type,
  }).join('\n');
}

function renderCode(block: EditorJsBlock, blockIndex: number): string {
  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  const code = getRequiredString(data, 'code', {
    blockIndex,
    blockType: block.type,
  });
  return `\`\`\`\n${code}\n\`\`\``;
}

function renderMermaid(block: EditorJsBlock, blockIndex: number): string {
  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  const code = getRequiredString(data, 'code', {
    blockIndex,
    blockType: block.type,
  });
  return `\`\`\`mermaid\n${code}\n\`\`\``;
}

function renderImage(block: EditorJsBlock, blockIndex: number): string {
  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  const file = getObjectRecord(data.file, {
    blockIndex,
    blockType: block.type,
    field: 'data.file',
  });
  const url = getRequiredString(file, 'url', {
    blockIndex,
    blockType: block.type,
  }).trim();
  if (!url) {
    fail('Conversion encountered an image without a URL.', {
      blockIndex,
      blockType: block.type,
      data,
    });
  }

  const caption =
    typeof data.caption === 'string' ? normalizePlainText(data.caption) : '';
  return `![${caption}](${url})`;
}

function renderTable(block: EditorJsBlock, blockIndex: number): string {
  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  const withHeadings = data.withHeadings;
  if (withHeadings !== true) {
    fail(
      'Conversion only supports Editor.js tables with headings because markdown tables require a header row.',
      {
        blockIndex,
        blockType: block.type,
        data,
      },
    );
  }

  const content = data.content;
  if (!Array.isArray(content) || content.length === 0) {
    fail('Conversion encountered a table without rows.', {
      blockIndex,
      blockType: block.type,
      data,
    });
  }

  const rows = content.map((row) => {
    if (!Array.isArray(row) || row.some((cell) => typeof cell !== 'string')) {
      fail('Conversion encountered a malformed table row.', {
        blockIndex,
        blockType: block.type,
        row,
      });
    }

    return row.map((cell) => normalizePlainText(cell));
  });

  const columnCount = rows[0]?.length ?? 0;
  if (columnCount === 0 || rows.some((row) => row.length !== columnCount)) {
    fail('Conversion encountered an irregular table shape.', {
      blockIndex,
      blockType: block.type,
      rows,
    });
  }

  const header = `| ${rows[0].map(escapePipe).join(' | ')} |`;
  const separator = `| ${rows[0].map(() => '---').join(' | ')} |`;
  const body = rows
    .slice(1)
    .map((row) => `| ${row.map(escapePipe).join(' | ')} |`);

  return [header, separator, ...body].join('\n');
}

function renderDocumentHeading(
  block: EditorJsBlock,
  blockIndex: number,
  displayPath: string,
  seenDocumentTitle: { current: boolean },
): string {
  if (seenDocumentTitle.current) {
    fail('Conversion found more than one document heading block.', {
      blockIndex,
      blockType: block.type,
      data: block.data,
    });
  }

  const data = getObjectRecord(block.data, {
    blockIndex,
    blockType: block.type,
    field: 'data',
  });
  const text = getRequiredString(data, 'text', {
    blockIndex,
    blockType: block.type,
  });
  const expectedTitle = normalizeTitleText(getExpectedTitle(displayPath));
  const actualTitle = normalizeTitleText(text);
  if (actualTitle !== expectedTitle) {
    fail(
      'Document title does not match the path-derived markdown title and cannot be converted safely.',
      {
        blockIndex,
        blockType: block.type,
        actualTitle,
        expectedTitle,
        data,
      },
    );
  }

  seenDocumentTitle.current = true;
  return NP_DOCUMENT_TITLE_TAG;
}

function renderBlock(
  block: EditorJsBlock,
  blockIndex: number,
  displayPath: string,
  seenDocumentTitle: { current: boolean },
): string {
  switch (block.type) {
    case 'documentHeading':
      return renderDocumentHeading(
        block,
        blockIndex,
        displayPath,
        seenDocumentTitle,
      );
    case 'paragraph':
      return renderParagraph(block, blockIndex);
    case 'header':
      return renderHeader(block, blockIndex);
    case 'list':
      return renderList(block, blockIndex);
    case 'code':
      return renderCode(block, blockIndex);
    case 'mermaid':
      return renderMermaid(block, blockIndex);
    case 'image':
      return renderImage(block, blockIndex);
    case 'table':
      return renderTable(block, blockIndex);
    case 'files':
      return serializeMarkdownFilesBlock({ data: {} });
    case 'gallery':
      return serializeMarkdownGalleryBlock({
        data: normalizeGalleryBlockData(block.data),
      });
    default:
      fail('Conversion encountered an unsupported block type.', {
        blockIndex,
        blockType: block.type,
        data: block.data,
      });
  }
}

export function convertEditorJsToMarkdown(params: {
  data: EditorJsData | null;
  displayPath: string;
}): string {
  const blocks = params.data?.blocks ?? [];
  const seenDocumentTitle = { current: false };
  const parts = blocks
    .map((block, index) =>
      renderBlock(block, index, params.displayPath, seenDocumentTitle).trim(),
    )
    .filter((value) => value.length > 0);

  if (!seenDocumentTitle.current) {
    parts.unshift(NP_DOCUMENT_TITLE_TAG);
  }

  return `${parts.join('\n\n').replace(/\r\n/g, '\n').trimEnd()}\n`;
}
